import React, { useEffect, useState } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem, OutlinedInput,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";

import {
  fetchHumanCapitalGoals, updateHumanCapitalGoal,
  fetchJobs,
  fetchStrategicIssues,
} from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import { useAuth } from "../../providers/AuthProvider";

export default function SmHumanCapitalGoalEditPage() {
  const { businessDomainId, successFactorId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const { companyId } = useAuth();
  const t = (id) => intl.formatMessage({ id });

  const [successFactorName, setSuccessFactorName] = useState("");
  const [successFactorDescription, setSuccessFactorDescription] = useState("");
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedNames, setSelectedNames] = useState([]);
  const [sourceIds, setSourceIds] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [strategicIssues, setStrategicIssues] = useState([]);
  const [fullData, setFullData] = useState(null);

  // 指標は選択されたジョブの indicators から取得
  const sourceIndicators = selectedJob?.indicators || [];

  useEffect(() => {
    (async () => {
      try {
        const [goals, jbs, issues] = await Promise.all([
          fetchHumanCapitalGoals(businessDomainId),
          fetchJobs(businessDomainId),
          fetchStrategicIssues(businessDomainId).catch(() => []),
        ]);
        setJobs(Array.isArray(jbs) ? jbs : []);
        setStrategicIssues(Array.isArray(issues) ? issues : []);
        const found = (Array.isArray(goals) ? goals : []).find(
          (g) => String(g.successFactorId) === String(successFactorId)
        );
        if (found) {
          setFullData(found);
          setSuccessFactorName(found.successFactorName || "");
          setSuccessFactorDescription(found.successFactorDescription || "");
          setSourceIds((found.sources || []).map((s) => s.successFactorId));
          setSelectedNames((found.indicators || []).map((ind) => ind.indicatorName));
          if (found.jobId) {
            const foundJob = (Array.isArray(jbs) ? jbs : []).find(
              (j) => String(j.jobId) === String(found.jobId)
            );
            if (foundJob) setSelectedJob(foundJob);
          }
        }
      } catch (e) {
        notify.apiError(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDomainId, successFactorId]);

  const handleJobChange = (e) => {
    const job = jobs.find((j) => j.jobId === e.target.value);
    setSelectedJob(job || null);
    setSelectedNames([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sourceIndicators.length > 0 && selectedNames.length === 0) {
      notify.error(t("financialGoal.indicatorRequired"));
      return;
    }
    try {
      const indicators = selectedNames
        .map((name) => sourceIndicators.find((ind) => ind.indicatorName === name))
        .filter(Boolean);
      const sources = sourceIds
        .map((id) => {
          const issue = strategicIssues.find((s) => s.successFactorId === id);
          if (!issue) return null;
          return { successFactorId: issue.successFactorId, successFactorName: issue.successFactorName };
        })
        .filter(Boolean);
      await updateHumanCapitalGoal({
        ...fullData,
        successFactorName,
        successFactorDescription,
        jobId: selectedJob?.jobId || "",
        indicators,
        sources,
        businessDomainId,
        companyId,
        successFactorId,
      });
      notify.success(t("message.update.success"));
      navigate(`/design/${businessDomainId}/strategy-map/human-capital-goals/${successFactorId}`);
    } catch (err) {
      notify.apiError(err);
    }
  };

  if (!fullData) return null;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t("humanCapitalGoal.edit.title")}
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              fullWidth required
              label={t("successFactor.name")}
              value={successFactorName}
              onChange={(e) => setSuccessFactorName(e.target.value)}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth multiline rows={3}
              label={t("successFactor.description")}
              value={successFactorDescription}
              onChange={(e) => setSuccessFactorDescription(e.target.value)}
            />
          </Grid>

          {jobs.length > 0 && (
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>{t("job.title")}</InputLabel>
                <Select
                  value={selectedJob?.jobId || ""}
                  label={t("job.title")}
                  onChange={handleJobChange}
                >
                  {jobs.map((j) => (
                    <MenuItem key={j.jobId} value={j.jobId}>
                      {j.jobName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("strategicIssue.list.title")}</InputLabel>
              <Select
                multiple
                value={sourceIds}
                onChange={(e) => setSourceIds(e.target.value)}
                input={<OutlinedInput label={t("strategicIssue.list.title")} />}
              >
                {strategicIssues.map((item) => (
                  <MenuItem key={item.successFactorId} value={item.successFactorId}>
                    {item.successFactorName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("financialGoal.selectIndicatorHint")}</InputLabel>
              <Select
                multiple
                disabled={sourceIndicators.length === 0}
                value={selectedNames}
                onChange={(e) => setSelectedNames(e.target.value)}
                input={<OutlinedInput label={t("financialGoal.selectIndicatorHint")} />}
              >
                {sourceIndicators.map((ind) => (
                  <MenuItem key={ind.indicatorName} value={ind.indicatorName}>
                    {ind.indicatorName}（{ind.unit}）
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit" disabled={!successFactorName.trim()}>
            {t("common.save")}
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate(`/design/${businessDomainId}/strategy-map/human-capital-goals/${successFactorId}`)}
          >
            {t("common.cancel")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
